import axios from "axios";
import { UEN_TOKEN_KEY, logoutUEN } from "../utils";

const local = 'http://localhost:3002';
const homologacao = 'https://homologacao-sistem-2-backend2.nfuimb.easypanel.host'
const host = 'https://api.uenestudantil.com.br'

export const currentServer = host;

const api = axios.create({
    baseURL: currentServer,
    timeout: 45000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(UEN_TOKEN_KEY);
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        // Rotas públicas de auth/recuperação: aqui um 401 significa
        // "credenciais/dados incorretos" (ex: recuperar-conta retorna 401 quando
        // CPF+data não conferem), NÃO token expirado. Não pode deslogar/redirecionar
        // — senão o erro pisca e a tela some antes do usuário ler.
        const rotaPublicaAuth = url.includes('/api/auth/')
            || url.includes('/api/loginestudantilemailpassw');
        // 401 desloga (token inválido/expirado), exceto nas rotas públicas acima.
        // No /api/profile, 404 (usuário não existe mais no banco) e 403 (conta
        // inativa) também deslogam: se não há usuário válido, não pode permanecer
        // no sistema. Escopado ao /api/profile pra não deslogar em 404 de outros
        // endpoints (ex: pagamento/CEP não encontrado).
        const profileInvalido = url.includes('/api/profile') && (status === 404 || status === 403);
        if (!rotaPublicaAuth && (status === 401 || profileInvalido)) {
            logoutUEN();
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

async function parseBlobError(blob) {
    try {
        const text = await blob.text();
        try {
            const errorData = JSON.parse(text);
            return new Error(errorData.erro || errorData.error || 'Erro desconhecido');
        } catch {
            return new Error(text?.slice(0, 200) || 'Resposta inválida do servidor');
        }
    } catch {
        return new Error('Resposta inválida do servidor');
    }
}

export const validarUserByCpfApi = async ({ cpf, signal } = {}) => {
    const response = await api.post('/api/validarpdf', { cpf }, {
        responseType: 'blob',
        signal,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/pdf, application/json'
        }
    });

    if (response.headers['content-type'] !== 'application/pdf') {
        throw await parseBlobError(response.data);
    }
    return response;
};

// Gera o PDF "oficial" da carteirinha (renderiza no servidor com canvas + jsPDF).
// Retorna application/pdf como blob. Use pra download — não confundir com a versão
// PNG que é só pra exibir no preview.
export const generatePdfCarteirinhaApi = async ({ cpf, signal } = {}) => {
    const response = await api.post(
        '/carteirinhaestudante',
        { cpf },
        {
            responseType: 'blob',
            signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/pdf, application/json',
            },
        }
    );
    if (response.headers['content-type'] !== 'application/pdf') {
        throw await parseBlobError(response.data);
    }
    return response;
};

export const getUserCarteirinhaByCpfPngApi = async ({ cpf, signal } = {}) => {
    const response = await api.post(
        '/carteirinhaestudantepng',
        { cpf },
        {
            responseType: 'blob',
            signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'image/png, application/json'
            }
        }
    );
    return response;
};

export const getUserCarteirinhaByCpfFisicaPngApi = async ({ cpf, signal } = {}) => {
    const response = await api.post(
        '/api/fisicapng',
        { cpf },
        {
            responseType: 'blob',
            signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'image/png, application/json'
            }
        }
    );
    return response;
};

export const gerarCarteiraImagemApi = async ({ cpf, signal } = {}) => {
    const response = await api.post(
        '/api/gerarcarteiraimagem',
        { cpf },
        {
            responseType: 'blob',
            signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'image/png, application/json'
            }
        }
    );
    return response;
};

export const ApiLoginUser = async (params) =>
    api.post('/api/loginestudantil', params);

export const LoginUserEmailPasswordApi = async ({ email, password }) =>
    api.post('/api/loginestudantilemailpassw', { email, password });

export const verifyEmailExist = async ({ userEmail }) =>
    api.post('/api/verifyEmailExist', { userEmail });

export const verifyCpfExist = async ({ cpf }) =>
    api.post('/api/verifyCpfExist', { cpf });

export const registerUserSistemUENApi = async (props) =>
    api.post('/api/registerUserSistemUEN', props);

// `cepDestino` + `codigoServico`: obrigatórios quando produto.frete=1. O back
// recalcula o frete da modalidade escolhida (PAC/SEDEX) via Correios e soma no
// total cobrado. Sem eles o back retorna 400 pra produto com frete.
// `dadosCadastro`: { instituicao, curso, escolaridade, foto, endereco? } coletado
// no checkout — o back salva e aplica na carteirinha quando o pagamento aprova.
export const createPixPaymentApi = async ({ productId, userId, cepDestino, codigoServico, dadosCadastro }) =>
    api.post('/api/create-pix-payment', { productId, userId, cepDestino, codigoServico, dadosCadastro });

// Calcula o frete via Correios e devolve as MODALIDADES disponíveis (PAC/SEDEX)
// com preço + prazo de cada, pro cliente escolher no checkout.
// Resposta: { cepOrigem, cepDestino, opcoes: [{ codigo, nome, valor, prazoUtilDias, valorFormatado }] }
export const calcularFreteApi = async ({ cepDestino }) =>
    api.post('/api/frete/calcular', { cepDestino });

// Secure Fields (PCI) — o cartão é tokenizado no NAVEGADOR pelo MercadoPago.js V2.
// O número/CVC nunca passam pelo nosso código: mandamos só o token + a bandeira.
// `token`: id do cardToken gerado por mp.fields.createCardToken(...)
// `paymentMethodId`: bandeira resolvida no front (visa/master/elo...)
// `paymentTypeId`: 'credit_card' | 'debit_card'
// `deviceId`: window.MP_DEVICE_SESSION_ID (fingerprint do security.js) — sobe o score do antifraude.
// `billingAddress`: { cep, rua, numero, complemento?, bairro, cidade, uf } — reduz recusa.
// `phone`: '31999999999' (DDD + número, só dígitos).
export const createCardPaymentApi = async ({
    productId,
    token,
    paymentMethodId,
    issuerId,      // banco emissor (vindo do Brick) — melhora aprovação
    installments,
    paymentTypeId,
    deviceId,
    billingAddress,
    phone,
    cepDestino,    // obrigatório quando produto.fisica=1
    codigoServico, // modalidade escolhida (PAC/SEDEX) — obrigatório quando produto.fisica=1
    dadosCadastro, // { instituicao, curso, escolaridade, foto, endereco? } — aplicado na aprovação
}) =>
    api.post('/api/create-card-payment', {
        productId,
        token,
        paymentMethodId,
        issuerId,
        installments,
        paymentTypeId,
        deviceId,
        billingAddress,
        phone,
        cepDestino,
        codigoServico,
        dadosCadastro,
    });

export const verifyPaymentsMercadoPagoApi = async ({ transactionId }) =>
    api.post('/api/payments/verifypaymentsmercadopago', { transactionId });

// Cancela um Pix em aberto do próprio usuário (libera pra outra compra).
// Se o Pix já tiver sido pago, o back não cancela — provisiona a carteirinha.
export const cancelPixPaymentApi = async ({ paymentId }) =>
    api.post('/api/payments/cancel', { paymentId });

export const getInfoAlunoByCpf = async ({ cpf, query = '', signal } = {}) =>
    api.get(`/api/getUserByCPF/${cpf}${query}`, { signal });

// Porta de validação pública: código de uso + data de nascimento + token do
// reCAPTCHA v2 (+ honeypot de reforço). Retorna { ok, cpf } pra abrir /validar/:cpf.
export const validarPorCodigoApi = async ({ codigoUso, dataNascimento, captchaToken, hp = '' }) =>
    api.post('/api/validar/por-codigo', { codigoUso, dataNascimento, captchaToken, hp });

export const getProfileApi = async ({ signal } = {}) =>
    api.get('/api/profile', { signal });

export const getPaymentStatusByIdApi = async ({ paymentId }) =>
    api.get(`/api/payments/status/${paymentId}`);

export const getPaymentDetailsApi = async ({ paymentId }) =>
    api.get(`/api/payments/getpaymentdetails/${paymentId}`);

export const getProdutos = async ({ signal } = {}) =>
    api.get('/produtos', { signal });

export const getProdutosDisponiveisApi = async ({ signal } = {}) =>
    api.get('/api/produtos-disponiveis', { signal });

export const listCarteirinhasByUserIdApi = async ({ signal } = {}) =>
    api.get('/api/listcarteirinhasbyuserid', { signal });

export const getImagemCarteirinhaByIdApi = async ({ idDaCarteirinha }) =>
    api.get(`/api/getcarteirinhaimage/${idDaCarteirinha}`);

export const getHistoricoPagamentoApi = async ({ signal } = {}) =>
    api.get(`/payments/history`, { signal });

export const updateCarteirinhaApi = async ({ carteirinhaId, dadosCarteirinha }) =>
    api.put(`/api/updateCarteirinha/${carteirinhaId}`, dadosCarteirinha);

export const saveImagemCarteirinhaApi = async ({ idDaCarteirinha, imagem, ano }) =>
    api.post(`/api/saveimagemcarteirinha/${idDaCarteirinha}`, { imagem, ano });

// Comprovante de matrícula (PDF ou imagem em base64 data URI). Reenvio
// sobrescreve o anterior (upsert por id_carteirinha no backend).
export const saveComprovanteMatriculaApi = async ({ idDaCarteirinha, arquivo, mimeType, ano }) =>
    api.post(`/api/comprovante-matricula/${idDaCarteirinha}`, { arquivo, mime_type: mimeType, ano });

// Recuperação de conta SEM email (usuários antigos só com CPF+dataNasc).
// Etapa 1 = verifica os 3 fatores + agenda email; Etapa 2 = confirma o token.
export const recuperarContaIniciarApi = async ({ cpf, dataNascimento, email, senha }) =>
    api.post('/api/auth/recuperar-conta/iniciar', { cpf, dataNascimento, email, senha });

export const recuperarContaConfirmarApi = async ({ token }) =>
    api.post('/api/auth/recuperar-conta/confirmar', { token });

export const recuperarContaStatusApi = async ({ cpf }) =>
    api.get(`/api/auth/recuperar-conta/status?cpf=${encodeURIComponent(cpf)}`);

export const getComprovanteMatriculaApi = async ({ idDaCarteirinha }) =>
    api.get(`/api/comprovante-matricula/${idDaCarteirinha}`);

// Estudante salva o endereço de entrega da carteirinha física.
// Backend marca status_cadastro='completo' e o modal some na próxima
// chamada de getProfileApi.
export const updateSolicitacaoImpressaoApi = async ({ impressaoId, endereco }) =>
    api.put(`/api/impressoes/${impressaoId}`, endereco);

// Lista as solicitações de carteirinha física do próprio estudante, com código
// de rastreio e situação — alimenta a tela de Rastreamento.
export const getMinhasSolicitacoesApi = async ({ signal } = {}) =>
    api.get('/api/minhas-solicitacoes', { signal });

/* --- AUTH: Recuperação de senha --- */

// Pede o envio do email com link de reset. Sempre 200 (backend nunca
// revela se o email existe).
export const requestPasswordResetApi = async ({ email }) =>
    api.post('/api/auth/password/request', { email });

// Confirma a troca de senha usando o token recebido por email.
export const confirmPasswordResetApi = async ({ token, password }) =>
    api.post('/api/auth/password/confirm', { token, password });

// Checa se o token é válido antes de mostrar o formulário.
export const validatePasswordResetTokenApi = async ({ token }) =>
    api.get(`/api/auth/password/validate?token=${encodeURIComponent(token)}`);

/* --- AUTH: Confirmação de email + opt-out --- */

// Confirma o email do usuário (token vindo do link do email).
export const confirmEmailApi = async ({ token }) =>
    api.post('/api/auth/email/confirm', { token });

// Checa se o token de confirmação é válido (sem consumir).
export const validateEmailTokenApi = async ({ token }) =>
    api.get(`/api/auth/email/validate?token=${encodeURIComponent(token)}`);

// Reenvia o email de confirmação (autenticado — usa o token JWT).
export const resendEmailVerificationApi = async () =>
    api.post('/api/auth/email/resend');

// Página /descadastrar-email — link assinado, não precisa de auth.
export const checkUnsubscribeApi = async ({ uid, sig }) =>
    api.get(`/api/auth/email/unsubscribe/check?uid=${encodeURIComponent(uid)}&sig=${encodeURIComponent(sig)}`);

export const unsubscribeEmailApi = async ({ uid, sig }) =>
    api.post('/api/auth/email/unsubscribe', { uid, sig });

export const resubscribeEmailApi = async ({ uid, sig }) =>
    api.post('/api/auth/email/resubscribe', { uid, sig });

export default api;
